if (!customElements.get('cart-drawer')) {
class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    
    // Subscribe to cart updates to check upsell visibility
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.cartData) {
        this.updateUpsellVisibility(event.cartData);
        this.updateFreeShippingProgress(event.cartData);
      }
    });
    
    // Wait for DOM to be ready before setting up cart icon
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setHeaderCartIconAccessibility();
      });
    } else {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        this.setHeaderCartIconAccessibility();
      }, 0);
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) {
      // Retry if element doesn't exist yet (max 10 retries)
      if (!this._cartIconRetryCount) {
        this._cartIconRetryCount = 0;
      }
      if (this._cartIconRetryCount < 10) {
        this._cartIconRetryCount++;
        setTimeout(() => {
          this.setHeaderCartIconAccessibility();
        }, 100);
      }
      return;
    }

    // Set up the element
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    
    // Remove href to prevent navigation
    cartLink.setAttribute('href', '#');
    
    // Remove any existing listeners by using a one-time setup flag
    if (cartLink.dataset.cartDrawerSetup === 'true') {
      return; // Already set up
    }
    cartLink.dataset.cartDrawerSetup = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(cartLink);
    });
    
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE' || event.code.toUpperCase() === 'ENTER') {
        event.preventDefault();
        event.stopPropagation();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    this.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      if (!summary.hasAttribute('role')) this.setSummaryAccessibility(summary);
    });
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Update upsell visibility when drawer opens
        if (!this.classList.contains('is-empty')) {
          // Fetch fresh cart data to check upsell
          fetch(`${routes.cart_url}.js`)
            .then((response) => response.json())
            .then((cartData) => {
              this.updateUpsellVisibility(cartData);
              this.updateFreeShippingProgress(cartData);
            })
            .catch((e) => {
              console.error('Error fetching cart for upsell:', e);
            });
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  checkUpsellVisibility() {
    // Fetch current cart state to check upsell visibility
    fetch(`${routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cartData) => {
        // Ensure we have the right structure
        if (cartData && !cartData.items && cartData.item_count !== undefined) {
          // This is the cart JSON API response, items should be in cartData.items
          // But if it's not there, try to get it from the response
        }
        this.updateUpsellVisibility(cartData);
        this.updateFreeShippingProgress(cartData);
      })
      .catch((e) => {
        console.error('Error fetching cart:', e);
      });
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
      // Update upsell visibility after cart update - use parsedState directly
      this.updateUpsellVisibility(parsedState);
      // Free shipping progress is already correct in the freshly-rendered
      // section HTML above; re-patching it here with parsedState risks
      // clobbering it with stale data if requests resolve out of order.
    }, 50);
  }

  updateUpsellVisibility(cartData) {
    // Wait a bit for DOM to update if cart was just re-rendered
    setTimeout(() => {
      const upsellSection = document.getElementById('CartDrawer-Upsell');
      if (!upsellSection) {
        // Retry if element doesn't exist yet (might be re-rendering)
        setTimeout(() => this.updateUpsellVisibility(cartData), 200);
        return;
      }

      // Handle different cart data structures
      let items = [];
      if (cartData && cartData.items) {
        items = cartData.items;
      } else if (Array.isArray(cartData)) {
        items = cartData;
      } else if (cartData && cartData.item_count !== undefined) {
        // This might be a cart object, try to get items
        items = cartData.items || [];
      }

      // Check if cart has exactly one item, it's a one-time purchase, and quantity is 1
      const hasSingleItem = items.length === 1;
      let isOneTimePurchase = false;
      let isSingleQuantity = false;

      if (hasSingleItem && items[0]) {
        const item = items[0];
        // Check for selling_plan_allocation in various possible locations
        isOneTimePurchase = !item.selling_plan_allocation && 
                           !item.selling_plan && 
                           (!item.properties || !item.properties._selling_plan);
        isSingleQuantity = item.quantity === 1;
      }

      const shouldShow = hasSingleItem && isOneTimePurchase && isSingleQuantity;

      if (shouldShow) {
        upsellSection.style.display = 'block';
        // Update variant ID in the button if it exists
        const upsellButton = upsellSection.querySelector('#CartDrawer-UpsellButton');
        if (upsellButton && items[0] && items[0].variant_id) {
          upsellButton.setAttribute('data-variant-id', items[0].variant_id);
        } else if (upsellButton && items[0] && items[0].id) {
          // Try variant id from item id
          upsellButton.setAttribute('data-variant-id', items[0].id);
        }
      } else {
        upsellSection.style.display = 'none';
      }
    }, 150);
  }

  updateFreeShippingProgress(cartData) {
    const freeShippingBar = document.getElementById('CartDrawer-FreeShippingBar');
    if (!freeShippingBar) return;

    const FREE_SHIPPING_THRESHOLD = 5000; // $50.00 in cents
    const cartTotal = cartData.total_price || 0;
    const remainingAmount = Math.max(0, FREE_SHIPPING_THRESHOLD - cartTotal);
    const progressPercentage = Math.min(100, (cartTotal / FREE_SHIPPING_THRESHOLD) * 100);
    const thresholdReached = cartTotal >= FREE_SHIPPING_THRESHOLD;

    // Update progress bar width
    const progressBar = freeShippingBar.querySelector('.cart-drawer__free-shipping-progress-bar');
    if (progressBar) {
      progressBar.style.width = `${progressPercentage}%`;
    }

    // Update message text
    const messageText = freeShippingBar.querySelector('.cart-drawer__free-shipping-message');
    if (messageText) {
      if (thresholdReached) {
        messageText.textContent = '🎉 You\'ve unlocked free shipping!';
        messageText.classList.add('cart-drawer__free-shipping-message--success');
        freeShippingBar.setAttribute('data-threshold-reached', 'true');
      } else {
        const remainingFormatted = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2
        }).format(remainingAmount / 100);
        messageText.innerHTML = `Add <strong>${remainingFormatted}</strong> more for free shipping`;
        messageText.classList.remove('cart-drawer__free-shipping-message--success');
        freeShippingBar.removeAttribute('data-threshold-reached');
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDiscountForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('.cart-drawer__discount-input');
    this.button = this.querySelector('.cart-drawer__discount-apply');
    this.message = this.querySelector('.cart-drawer__discount-message');

    this.button.addEventListener('click', this.onApply.bind(this));
    this.input.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.onApply();
    });
  }

  setMessage(text, isError) {
    if (!this.message) return;
    this.message.textContent = text || '';
    this.message.hidden = !text;
    this.message.classList.toggle('cart-drawer__discount-message--error', !!isError);
  }

  onApply() {
    const code = (this.input.value || '').trim();

    if (!code) {
      this.setMessage(window.cartStrings.discountEmpty, true);
      this.input.focus();
      return;
    }

    this.button.setAttribute('disabled', true);
    this.button.classList.add('loading');
    this.setMessage('');

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionsToRender = cartDrawer && cartDrawer.getSectionsToRender
      ? cartDrawer.getSectionsToRender()
      : [{ id: 'cart-drawer' }];

    fetch(`${routes.cart_update_url}`, {
      ...fetchConfig(),
      body: JSON.stringify({
        discount: code,
        sections: sectionsToRender.map((section) => section.id),
        sections_url: window.location.pathname,
      }),
    })
      .then((response) => response.json())
      .then((state) => {
        const applied = (state.discount_codes || []).some(
          (discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable
        );

        if (applied && cartDrawer && cartDrawer.renderContents) {
          cartDrawer.renderContents(state);
        } else if (!applied) {
          this.setMessage(window.cartStrings.discountInvalid, true);
        }
      })
      .catch(() => {
        this.setMessage(window.cartStrings.discountError, true);
      })
      .finally(() => {
        this.button.removeAttribute('disabled');
        this.button.classList.remove('loading');
      });
  }
}

customElements.define('cart-discount-form', CartDiscountForm);

// Global event delegation as fallback for cart icon bubble
document.addEventListener('click', function(event) {
  const cartIcon = event.target.closest('#cart-icon-bubble');
  if (cartIcon) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      event.preventDefault();
      event.stopPropagation();
      cartDrawer.open(cartIcon);
    }
  }
}, true); // Use capture phase to catch early
}
